import {HttpErrorResponse} from '@angular/common/http';
import {Component, signal} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {createFormGroup, createFormlyOptions} from '@app/resource-explorer/resource.form.config';
import {ProjectCreate, ProjectRoleID, ProjectsService} from '@backend';
import {prepareFormPayload} from '@forms/utils/form-utils';
import {ValidationErrorHandler} from '@forms/validationerrorhandler.service';
import {IndexService} from '@middleware';
import {FormlyFieldConfig, FormlyFormOptions, FormlyModule} from '@ngx-formly/core';
import {PROJECT_PII_ATTESTATION} from '@project/project-details/project-details.component';
import {buildProjectCreateFields} from '@project/project.form.config';
import {defaultProjectCreate, mapFormModelToProjectCreate} from '@project/project.form.mappers';
import {AttestationService} from '@services/attestation/attestation.service';
import {UserService} from '@services/user/user.service';
import {SectionHeaderActions} from '@shared/components/section-header-actions/section-header-actions';
import {firstValueFrom} from 'rxjs';

@Component({
  selector: 'app-project-add',
  imports: [ReactiveFormsModule, FormlyModule, MatIconModule, MatButtonModule, RouterLink],
  templateUrl: './project-add.component.html',
})
export class ProjectAddComponent implements SectionHeaderActions {
  form: FormGroup = createFormGroup();
  options: FormlyFormOptions = createFormlyOptions();
  formFields: FormlyFieldConfig[] = [];
  model: ProjectCreate;
  submitting = false;
  projectsService!: ProjectsService;

  constructor(
    readonly userService: UserService,
    private readonly snackbarService: MatSnackBar,
    private readonly indexService: IndexService,
    private readonly router: Router,
    private readonly attestationService: AttestationService,
    private readonly validationErrorHandler: ValidationErrorHandler,
    private readonly route: ActivatedRoute,
  ) {
    this.formFields = buildProjectCreateFields(this.userService.userSignal().organization.full_name);
    this.model = defaultProjectCreate();

    this.route.data.subscribe(d => {
      const projectAddData = d['projectAddData'];
      if (projectAddData?.projectsService) {
        this.projectsService = projectAddData.projectsService;
      }
    });
  }

  public sectionHeaderActions = signal([
    {
      target: '/help/private-data-enclave/add-private-project',
      targetTab: '_blank',
      caption: 'Help',
      color: 'var(--mat-sys-primary)',
      icon: 'help_outline',
    },
  ]);

  async submit(event: Event): Promise<void> {
    event.preventDefault();
    if (this.form.status == 'PENDING' || this.projectsService == undefined) {
      this.snackbarService.open("We're making sure everything's alright, try again in a few seconds.", undefined, {
        duration: 5000,
      });
      return;
    } else if (!this.form.valid) {
      this.validationErrorHandler.handleError(this.formFields);
      return;
    }
    if (!(await this.attestationService.confirmWithCancel(PROJECT_PII_ATTESTATION))) return;
    try {
      this.submitting = true;
      const sanitizedForm = await prepareFormPayload(this.model, this.formFields);
      sanitizedForm.users = [
        {
          email: this.userService.userSignal().email,
          role: ProjectRoleID.OWNER,
          is_project_pi: false, // if the user is a PI, the next function will make that happen.
        },
      ];
      const payload = mapFormModelToProjectCreate(sanitizedForm);
      const response = await firstValueFrom(this.projectsService.createProject(payload, {observe: 'response'}));

      // Because we're observing the response and not the model object itself, Angular won't automatically
      // throw errors. We do so manually to preserve validation error handling.
      if (!response.ok)
        throw new HttpErrorResponse({
          status: response.status,
          url: response.url,
          headers: response.headers,
          error: response.body,
        });

      const location = response.headers.get('Location');
      const id = location?.split('/').pop();

      // For project add, use the returned ID and the creator's organization to sync the project:
      await firstValueFrom(
        this.indexService.syncProjectInIndex(id, {
          storage_organization: this.userService.userSignal().organization.name,
        }),
      );

      this.form.markAsPristine();
      (this as any).__skipUnsavedGuard = true;

      await this.router.navigate(['/private-data-enclave/project/view', id]);
    } catch (e) {
      this.validationErrorHandler.handleError(this.formFields, <HttpErrorResponse>e);
    } finally {
      this.submitting = false;
    }
  }
}
